import type { GrantCode } from '../../auth/grants'
import AttachmentDownloadButton from './AttachmentDownloadButton'
import { formatBytes } from '../../lib/fileSize'

/** İndirilebilir dosya eki (id + ad + boyut + uzantı). İçerik indirme ortak uçtan: /api/v1/management/attachments/{id}. */
export interface AttachmentItem {
  id: number
  fileName: string
  size: number
  extension?: string | null
}

interface Props {
  /** Bölüm başlığı (ör. "Tutanaklar" / "Dosya Ekleri"). */
  title: string
  items: AttachmentItem[]
  /** Boşken gösterilecek metin; verilmezse boş liste hiç gösterilmez (çağıran gizler). */
  emptyText?: string
  /** İndirme için gereken yetki (bağlama göre) — `AttachmentDownloadButton`'a geçirilir. */
  requiredGrant: GrantCode
}

/** Uzantıya göre ikon renk sınıfı (nötr/tür-bazlı ton). */
function fileIcon(ext?: string | null): string {
  const e = (ext || '').toLowerCase()
  if (e === 'pdf') return 'is-pdf'
  if (['xls', 'xlsx', 'csv'].includes(e)) return 'is-sheet'
  if (['doc', 'docx'].includes(e)) return 'is-doc'
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(e)) return 'is-img'
  return 'is-file'
}

/** Uzantıya göre PrimeIcons glifi. */
function fileIconGlyph(ext?: string | null): string {
  const e = (ext || '').toLowerCase()
  if (e === 'pdf') return 'pi-file-pdf'
  if (['xls', 'xlsx', 'csv'].includes(e)) return 'pi-file-excel'
  if (['doc', 'docx'].includes(e)) return 'pi-file-word'
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(e)) return 'pi-image'
  return 'pi-file'
}

/**
 * Paylaşılan dosya eki listesi (Reuse Mandate) — ad + tür/boyut + **indir** butonu; ExecutiveSummary
 * "Dosya Ekleri" tarzıyla birebir. Yalnız başlık çağırandan gelir; indirme ortak uçtan. Boşken `emptyText`
 * verilirse kısa not gösterir (verilmezse çağıran bölümü gizler).
 */
export default function AttachmentList({ title, items, emptyText, requiredGrant }: Readonly<Props>) {
  return (
    <div className="ui-attach">
      <div className="ui-attach-head">
        <h3 className="ui-attach-title">{title}</h3>
        {items.length > 0 && <span className="ui-attach-count">{items.length}</span>}
      </div>
      {items.length === 0 ? (
        emptyText ? <p className="ui-attach-empty">{emptyText}</p> : null
      ) : (
        <ul className="ui-attach-list">
          {items.map((f) => (
            <li className="ui-attach-item" key={f.id}>
              <span className={`ui-attach-icon ${fileIcon(f.extension)}`} aria-hidden="true">
                <i className={`pi ${fileIconGlyph(f.extension)}`} />
              </span>
              <span className="ui-attach-meta">
                <span className="ui-attach-name" title={f.fileName}>
                  {f.fileName}
                </span>
                <span className="ui-attach-sub">
                  {(f.extension || '').toUpperCase()}
                  {f.extension ? ' · ' : ''}
                  {formatBytes(f.size)}
                </span>
              </span>
              <AttachmentDownloadButton id={f.id} fileName={f.fileName} requiredGrant={requiredGrant} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
